export type CookieConsent = {
  necessary: true;
  analytics: boolean;
  timestamp: number;
};

const CONSENT_KEY = "lucentia_cookie_consent";
const CONSENT_MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000;

type ConsentListener = (consent: CookieConsent | null) => void;

const listeners = new Set<ConsentListener>();

function parseConsent(raw: string | null): CookieConsent | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<CookieConsent>;
    if (typeof parsed.analytics !== "boolean" || typeof parsed.timestamp !== "number") {
      return null;
    }
    if (Date.now() - parsed.timestamp > CONSENT_MAX_AGE_MS) {
      return null;
    }
    return { necessary: true, analytics: parsed.analytics, timestamp: parsed.timestamp };
  } catch {
    return null;
  }
}

export function getCookieConsent(): CookieConsent | null {
  if (typeof window === "undefined") return null;
  try {
    return parseConsent(localStorage.getItem(CONSENT_KEY));
  } catch {
    return null;
  }
}

export function hasAnalyticsConsent(): boolean {
  return getCookieConsent()?.analytics === true;
}

export function hasConsentChoice(): boolean {
  return getCookieConsent() !== null;
}

function persistConsent(analytics: boolean): CookieConsent {
  const consent: CookieConsent = {
    necessary: true,
    analytics,
    timestamp: Date.now(),
  };
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
  } catch {
    /* ignore storage errors */
  }
  listeners.forEach((fn) => fn(consent));
  return consent;
}

export function acceptAllCookies(): CookieConsent {
  return persistConsent(true);
}

export function rejectNonEssentialCookies(): CookieConsent {
  clearAnalyticsStorage();
  return persistConsent(false);
}

export function setAnalyticsConsent(analytics: boolean): CookieConsent {
  if (!analytics) clearAnalyticsStorage();
  return persistConsent(analytics);
}

export function clearAnalyticsStorage(): void {
  try {
    localStorage.removeItem("lucentia_analytics_visitor");
    sessionStorage.removeItem("lucentia_analytics_session");
  } catch {
    /* ignore */
  }
}

export function resetCookieConsent(): void {
  try {
    localStorage.removeItem(CONSENT_KEY);
  } catch {
    /* ignore */
  }
  clearAnalyticsStorage();
  listeners.forEach((fn) => fn(null));
}

export function subscribeCookieConsent(listener: ConsentListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export const COOKIE_CONSENT_KEY = CONSENT_KEY;
